import * as amplitude from "@amplitude/analytics-browser";
import { ClickAwayListener } from "@mui/material";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import LightningIcon from "@redotech/redo-icons/lightning-01.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomerSearch } from "@redotech/redo-merchant-app-common/client/shopify";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  ConversationPlatform,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import {
  CustomerTagWithId,
  getPrimaryCustomerEmail,
  getPrimaryCustomerFacebook,
  getPrimaryCustomerInstagram,
  getPrimaryCustomerPhoneNumber,
  getPrimaryCustomerShopifyCustomerId,
} from "@redotech/redo-model/customer";
import { Order } from "@redotech/redo-model/order";
import {
  OrderManagementPermissions,
  SupportPermissions,
} from "@redotech/redo-model/permissions";
import { Return } from "@redotech/redo-model/return";
import { Team } from "@redotech/redo-model/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Divider } from "@redotech/redo-web/divider";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { ExternalLinkButton } from "@redotech/redo-web/external-link-button";
import { Flex } from "@redotech/redo-web/flex";
import { ExternalLink } from "@redotech/redo-web/link";
import ShopifyBagLogo from "@redotech/redo-web/shopify/bag-logo.svg";
import { Text } from "@redotech/redo-web/text";
import { DATA_VISUALIZATION_COLORS } from "@redotech/redo-web/theme/colors";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { filterTruthy, unique } from "@redotech/util/array";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { updateConversation } from "../client/conversations";
import { useCustomerSuggestions } from "../hooks/use-customer-suggestions";
import { ShopifyCustomer } from "../new-message-modal";
import * as conversationDetailsCustomerCss from "./conversation-details-customer.module.css";
import { CustomerTagInput } from "./customer-tag-input";
import { CustomerTotals } from "./customer-totals";

const NAME_EMAIL_DELIMITER = " - ";
const SUGGESTION_ICON_COLOR = DATA_VISUALIZATION_COLORS.orangeDark[300];

export const ConversationDetailsCustomer = memo(
  function ConversationDetailsCustomer({
    conversation,
    orders,
    returns,
    doOrdersLoad,
    doReturnsLoad,
    doCartsLoad,
    setActiveConversation,
  }: {
    conversation: MinimalConversation;
    orders: Order[] | undefined;
    returns: Return[] | undefined;
    doOrdersLoad: () => void;
    doReturnsLoad: () => void;
    doCartsLoad: () => void;
    setActiveConversation: (conversation: MinimalConversation) => void;
  }) {
    const customer = conversation.customer;
    const client = useRequiredContext(RedoMerchantClientContext);
    const team = useRequiredContext(TeamContext);
    const user = useContext(UserContext);

    const primaryCustomerEmail = getPrimaryCustomerEmail(customer);
    const primaryCustomerPhone = getPrimaryCustomerPhoneNumber(customer);
    const primaryCustomerInstagram = getPrimaryCustomerInstagram(customer);
    const primaryCustomerFacebook = getPrimaryCustomerFacebook(customer);

    const canViewOrders = useHasPermission(
      OrderManagementPermissions.VIEW_ORDER,
    );
    const canViewReturns = useHasPermission(
      OrderManagementPermissions.VIEW_RETURN,
    );

    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
      null,
    );
    const [editDropdownOpen, setEditDropdownOpen] = useState(false);
    const [firstName, setFirstName] = useState<string | null>();
    const [lastName, setLastName] = useState<string | null>();
    const canEditCustomer = useHasPermission(SupportPermissions.EDIT_CUSTOMER);

    const customerSuggestions = useCustomerSuggestions(conversation);

    const [searchString, setSearchString] = useState<string | undefined>();
    const debouncedSearch = useDebounce(searchString, 500);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customers, setCustomers] = useState<Record<string, ShopifyCustomer>>(
      {},
    );

    const [customerTagsToShow, setCustomerTagsToShow] = useState<
      CustomerTagWithId[]
    >([]);

    useEffect(() => {
      setCustomerTagsToShow(conversation.customer?.tagIds || []);
    }, [conversation]);

    const profileImageUrl = useMemo(() => {
      if (primaryCustomerInstagram?.profilePic) {
        return primaryCustomerInstagram.profilePic;
      }
      if (primaryCustomerFacebook?.profilePic) {
        return primaryCustomerFacebook.profilePic;
      }
      if (customer?.image?.url) {
        return customer.image.url;
      }
      if (!primaryCustomerEmail) {
        return null;
      }
      return getUserAvatarUrl({
        email: primaryCustomerEmail || "",
        userId: user?._id,
        defaultImage: "404",
        size: Math.ceil(devicePixelRatio * 32),
      });
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      primaryCustomerInstagram?.profilePic,
      primaryCustomerFacebook?.profilePic,
      customer?.image?.url,
      devicePixelRatio,
      primaryCustomerEmail,
    ]);

    const [customersLoad, doCustomersLoad] = useTriggerLoad(async (signal) => {
      const searchTermPieces = (debouncedSearch || "").split(
        NAME_EMAIL_DELIMITER,
      );
      const searchTerm = searchTermPieces[searchTermPieces.length - 1];
      const customers = await getCustomerSearch(client, {
        search: searchTerm,
        signal,
      });
      setCustomersLoading(false);
      return customers;
    });
    useEffect(() => {
      if (searchString !== undefined) {
        setCustomersLoading(true);
        doCustomersLoad();
      }
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    useEffect(() => {
      if (customersLoad.value?.customers) {
        setCustomers(
          customersLoad.value.customers.reduce(
            (accumulator: Record<string, ShopifyCustomer>, customer) => {
              accumulator[customer.email!] = {
                email: customer.email!,
                firstName: customer.first_name!,
                lastName: customer.last_name!,
                smsOptedIn: !!(
                  customer.supportCommunicationConsent?.textMessages
                    ?.optInDate &&
                  !customer.supportCommunicationConsent?.textMessages
                    ?.optOutDate
                ),
              };
              return accumulator;
            },
            {},
          ),
        );
      }
    }, [customersLoad.value]);

    const handleCustomerChange = async (newEmail: string) => {
      const newCustomer = {
        email: newEmail || undefined,
        firstName:
          firstName ||
          (newEmail ? customers[newEmail]?.firstName || undefined : undefined),
        lastName:
          lastName ||
          (newEmail ? customers[newEmail]?.lastName || undefined : undefined),
      };
      if (newEmail) {
        const result = await updateConversation(client, conversation, {
          customer: newCustomer,
        });
        amplitude.logEvent("update-conversationCustomer", {
          conversationId: conversation._id,
          channel: conversation.platform,
          customer: newCustomer?.email,
        });
        setActiveConversation({
          ...conversation,
          customer: result.data.customer,
        });
        if (canViewOrders) {
          doOrdersLoad();
        }
        if (canViewReturns) {
          doReturnsLoad();
        }
        doCartsLoad();
        setEditDropdownOpen(false);
        setFirstName(null);
        setLastName(null);
      }
    };

    const getCustomerDisplayName = useHandler((email: string) => {
      if (email === "none") {
        return "No customer";
      }
      if (email.includes(NAME_EMAIL_DELIMITER)) {
        return email;
      }

      if (email === primaryCustomerEmail) {
        return `${customer.name}${NAME_EMAIL_DELIMITER}${primaryCustomerEmail}`;
      }
      if (!customers[email]) {
        return `New customer${NAME_EMAIL_DELIMITER}${email}`;
      }
      if (
        !customers[email]?.firstName &&
        !customers[email]?.lastName &&
        !customers[email]?.email
      ) {
        return "";
      }
      if (!customers[email]?.firstName && !customers[email]?.lastName) {
        return `Missing name${NAME_EMAIL_DELIMITER}${customers[email]?.email || ""}`;
      }
      return `${customers[email]?.firstName || ""} ${
        customers[email]?.lastName || ""
      }${NAME_EMAIL_DELIMITER}${customers[email]?.email || ""}`;
    });

    function handleViewCustomerOnShopify(url: string): void {
      window.open(url, "_blank");
    }

    const [focusedIndex, setFocusedIndex] = useState<number | undefined>();
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

    const closeDropdown = () => {
      setEditDropdownOpen(false);
      setSearchString("");
    };

    const customerInput = (
      <input
        autoFocus
        className={conversationDetailsCustomerCss.input}
        onChange={(event) => setSearchString(event.target.value)}
        placeholder="Search by email, name, or order #..."
        ref={setInputRef}
        value={searchString}
      />
    );

    const shopifyUrl = shopifyCustomerLink(team, conversation, orders || []);

    const filteredSuggestions = useMemo(() => {
      return customerSuggestions.suggestions.filter(
        (suggestion) => !customer || suggestion.customer._id !== customer._id,
      );
    }, [customerSuggestions.suggestions, customer]);

    const customerOptions: RedoListItem<string>[] = useMemo(() => {
      const items: RedoListItem<string>[] = [];

      if (!searchString && filteredSuggestions.length > 0) {
        items.push({
          id: "suggestions-header",
          value: "suggestions-header",
          type: "custom",
          useWrapper: false,
          render: (
            <Flex align="center" gap="xs">
              <LightningIcon
                style={{
                  width: "14px",
                  height: "14px",
                  color: SUGGESTION_ICON_COLOR,
                  fill: SUGGESTION_ICON_COLOR,
                }}
              />
              <Text fontSize="xs" style={{ opacity: 1 }} textColor="tertiary">
                Suggestions
              </Text>
            </Flex>
          ),
        });

        const displayableSuggestions = filteredSuggestions
          .filter((s) => s.customer.emails?.[0]?.email)
          .slice(0, 3);

        for (const suggestion of displayableSuggestions) {
          const primaryEmail = suggestion.customer.emails?.[0]?.email;
          if (primaryEmail) {
            items.push({
              id: `suggestion-${suggestion.customer._id}`,
              value: primaryEmail,
              type: "text",
              text: `${suggestion.customer.name || "Unknown"} - ${primaryEmail} - ${Math.round(suggestion.score * 100)}% match`,
              leadingItem: {
                type: "avatar",
                alt: "Customer profile picture",
                imageUrl: getUserAvatarUrl({ email: primaryEmail }),
                name: suggestion.customer.name,
              },
            });
          }
        }
      }

      const customerEmails = Object.keys(customers);
      const searchResultItems = customerEmails.map<RedoListItem<string>>(
        (email) => ({
          id: email,
          value: email,
          type: "text",
          text: getCustomerDisplayName(email),
          leadingItem: {
            type: "avatar",
            alt: "User profile picture",
            imageUrl: getUserAvatarUrl({ email }),
            name: `${customers[email]?.firstName} ${customers[email]?.lastName}`,
          },
        }),
      );

      return [...items, ...searchResultItems];
    }, [customers, getCustomerDisplayName, searchString, filteredSuggestions]);

    return (
      <div className={conversationDetailsCustomerCss.details}>
        <Flex dir="column" gap="xs">
          <ClickAwayListener
            onClickAway={() => {
              if (editDropdownOpen) {
                closeDropdown();
              }
            }}
          >
            <div>
              <Flex align="center" justify="space-between">
                <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                  Customer
                </Text>
              </Flex>
              <Flex align="center" gap="xxs" ref={setDropdownAnchor}>
                {!customer && filteredSuggestions.length > 0 ? (
                  <Tooltip
                    placement="top"
                    title="Click to assign this suggested customer"
                  >
                    <div
                      className={conversationDetailsCustomerCss.suggestionBadge}
                      onClick={() => {
                        if (
                          !canEditCustomer ||
                          [
                            ConversationPlatform.INSTAGRAM,
                            ConversationPlatform.FACEBOOK,
                            ConversationPlatform.TIKTOK,
                          ].includes(conversation.platform)
                        ) {
                          return;
                        }
                        setEditDropdownOpen(true);
                      }}
                    >
                      <LightningIcon
                        style={{
                          width: "12px",
                          height: "12px",
                          color: SUGGESTION_ICON_COLOR,
                          fill: SUGGESTION_ICON_COLOR,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        className={
                          conversationDetailsCustomerCss.suggestionText
                        }
                      >
                        Maybe:{" "}
                        {filteredSuggestions[0].customer.name || "Unknown"}
                      </span>
                    </div>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip
                      className={
                        [
                          ConversationPlatform.INSTAGRAM,
                          ConversationPlatform.FACEBOOK,
                          ConversationPlatform.TIKTOK,
                        ].includes(conversation.platform)
                          ? undefined
                          : (customer?.name?.length || 0) <= 15
                            ? conversationDetailsCustomerCss.hidden
                            : undefined
                      }
                      placement="top"
                      title={
                        [
                          ConversationPlatform.INSTAGRAM,
                          ConversationPlatform.FACEBOOK,
                          ConversationPlatform.TIKTOK,
                        ].includes(conversation.platform)
                          ? "Cannot change customer for this channel at this time."
                          : customer?.name
                      }
                    >
                      <div
                        className={conversationDetailsCustomerCss.customerBadge}
                        onClick={() => {
                          if (
                            !canEditCustomer ||
                            [
                              ConversationPlatform.INSTAGRAM,
                              ConversationPlatform.FACEBOOK,
                              ConversationPlatform.TIKTOK,
                            ].includes(conversation.platform)
                          ) {
                            return;
                          }
                          amplitude.logEvent("view-updateCustomerModal", {
                            conversationId: conversation._id,
                            channel: conversation.platform,
                          });
                          setEditDropdownOpen(true);
                        }}
                      >
                        <RedoBadge
                          avatar={{
                            alt: "Customer profile picture",
                            name: customer?.name,
                            imageUrl: profileImageUrl,
                          }}
                          color="gray"
                          size="sm"
                          text={customer?.name || "none"}
                        />
                      </div>
                    </Tooltip>
                    {customer && filteredSuggestions.length > 0 && (
                      <Tooltip title="Customer suggestions available">
                        <LightningIcon
                          style={{
                            width: "16px",
                            height: "16px",
                            color: SUGGESTION_ICON_COLOR,
                            fill: SUGGESTION_ICON_COLOR,
                          }}
                        />
                      </Tooltip>
                    )}
                  </>
                )}
                <div>
                  <Dropdown
                    anchor={dropdownAnchor}
                    fitToAnchor={false}
                    open={editDropdownOpen}
                  >
                    <Flex
                      className={
                        conversationDetailsCustomerCss.dropdownContainer
                      }
                      dir="column"
                      gap="sm"
                    >
                      {customerInput}
                      <Divider />
                      <RedoList
                        focusedIndex={focusedIndex}
                        items={customerOptions}
                        itemSelected={async ({ item }) => {
                          closeDropdown();
                          await handleCustomerChange(item.value);
                        }}
                        itemsLoading={customersLoading}
                        refToListenTo={inputRef}
                        selectedItems={primaryCustomerEmail}
                        setFocusedIndex={setFocusedIndex}
                        size="sm"
                      />
                    </Flex>
                  </Dropdown>
                </div>
                {customer?.customer && (
                  <ExternalLinkButton
                    url={`${process.env.MERCHANT_APP_URL}/stores/${team._id}/customer/${customer.customer}`}
                  />
                )}
              </Flex>
            </div>
          </ClickAwayListener>
        </Flex>
        <Flex dir="column">
          {primaryCustomerEmail && (
            <div>
              <div className={conversationDetailsCustomerCss.copyableField}>
                <div className={conversationDetailsCustomerCss.email}>
                  <CopyableText
                    fontSize="xs"
                    fontWeight="medium"
                    textColor="tertiary"
                    textOverflow="ellipsis"
                  >
                    {primaryCustomerEmail}
                  </CopyableText>
                </div>
              </div>
            </div>
          )}

          {primaryCustomerPhone && (
            <CopyableText
              fontSize="xs"
              fontWeight="medium"
              textColor="tertiary"
            >
              {primaryCustomerPhone}
            </CopyableText>
          )}
          {[
            ConversationPlatform.INSTAGRAM,
            ConversationPlatform.INSTAGRAM_COMMENTS,
          ].includes(conversation.platform) &&
            primaryCustomerInstagram?.username && (
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                <ExternalLink
                  className={conversationDetailsCustomerCss.username}
                  showIcon={false}
                  url={`https://instagram.com/${primaryCustomerInstagram.username}`}
                >
                  @{primaryCustomerInstagram.username}
                </ExternalLink>
              </Text>
            )}
        </Flex>
        {conversation.customer?.customer && (
          <Flex dir="column">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Shopify customer tags
            </Text>
            <CustomerTagInput
              currentTags={customerTagsToShow}
              customerId={conversation.customer.customer}
              setCurrentTags={(tags) => setCustomerTagsToShow(tags)}
              showAddButtonToEnterInput
              showBorder={false}
            />
          </Flex>
        )}

        {(orders || returns) && (
          <CustomerTotals orders={orders} returns={returns} />
        )}
        {shopifyUrl && (
          <RedoButton
            hierarchy="secondary"
            IconLeading={ShopifyBagLogo}
            onClick={() => handleViewCustomerOnShopify(shopifyUrl)}
            size="xs"
            text="View on Shopify"
          />
        )}
      </div>
    );
  },
);

function shopifyCustomerId(
  conversation: MinimalConversation,
  orders: Order[],
): string | undefined {
  const customerIdFromConversation = getPrimaryCustomerShopifyCustomerId(
    conversation.customer,
  );
  if (customerIdFromConversation) {
    if (customerIdFromConversation.includes("gid://shopify/Customer/")) {
      return customerIdFromConversation.split("gid://shopify/Customer/")[1];
    }
    return customerIdFromConversation;
  }

  const customerIdFromOrders = unique(
    filterTruthy(orders?.map((order) => order.shopify.customer?.id)),
  );
  return customerIdFromOrders[0];
}

function shopifyCustomerLink(
  team: Team,
  conversation: MinimalConversation,
  orders: Order[],
): string | undefined {
  const id = shopifyCustomerId(conversation, orders);
  if (!id) {
    return undefined;
  }
  const regexMatch = team.storeUrl?.match(/(.*)\.myshopify.com/);
  const storeName = regexMatch ? regexMatch[1] : undefined;
  if (!storeName) {
    return;
  }
  return `https://admin.shopify.com/store/${storeName}/customers/${id}`;
}
